// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "../../../theme/SwiperOverrides.css";

import {
	A11y,
	Navigation,
	Pagination,
	Scrollbar,
	Virtual,
} from "swiper/modules";
import {
	Swiper,
	type SwiperClass,
	type SwiperProps,
	SwiperSlide,
} from "swiper/react";

import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useClientSide } from "../../../hooks/useClientSide";
import { useIsTouchDevice } from "../../../hooks/useIsTouchDevice";
import type { HeroImageProps } from "../../HeroImage/HeroImage";
import SwiperImage from "../../SwiperImage/SwiperImage";

export interface MediaImageProps {
	type: "image";
	src: string;
	alt: string;
}

export interface MediaVideoProps {
	title: string;
	type: "video";
	youtubeId?: string;
	url?: string;
}

export interface IntroRehomingCentreProps extends SwiperProps {
	previewImageIndex?: number;
	galleryItems: (MediaImageProps | MediaVideoProps)[];
	className: string;
	CustomImageComponent?: React.FC<HeroImageProps>;
	CustomVideoComponent?: React.FC<MediaVideoProps>;
	hidePagination?: boolean;
	isDog?: boolean;
}

interface MediaCarouselRef {
	swiper: SwiperClass;
}
export const MediaCarousel = forwardRef<
	MediaCarouselRef,
	IntroRehomingCentreProps
>(
	(
		{
			previewImageIndex,
			galleryItems,
			className,
			CustomImageComponent,
			CustomVideoComponent,
			hidePagination = false,
			isDog,
			...props
		},
		ref,
	) => {
		const [swiper, setSwiper] = useState<SwiperClass>(null);

		const ImageWrapper = CustomImageComponent || SwiperImage;
		const touchDevice = useIsTouchDevice();

		const isClientSide = useClientSide();

		useImperativeHandle(
			ref,
			() => ({
				swiper,
			}),
			[swiper],
		);

		if (!isClientSide) {
			const previewMedia = galleryItems[previewImageIndex];
			if (!previewMedia) return null;
			return (
				<div className={className}>
					{previewMedia.type === "image" ? (
						<ImageWrapper {...previewMedia} />
					) : (
						<CustomVideoComponent {...previewMedia} />
					)}
				</div>
			);
		}

		return (
			<Swiper
				key={"swiper"}
				onSwiper={(swiper) => {
					setSwiper(swiper);
				}}
				className={className}
				modules={
					hidePagination
						? [Virtual, Navigation, A11y, Scrollbar]
						: [Virtual, Navigation, A11y, Scrollbar, Pagination]
				}
				spaceBetween={0}
				slidesPerView={1}
				edgeSwipeThreshold={10}
				threshold={0}
				grabCursor={true}
				longSwipes={true}
				resistance={false}
				shortSwipes={!!touchDevice}
				speed={600}
				loop={true}
				effect="slide"
				direction="horizontal"
				centeredSlides={true}
				pagination={{ clickable: true }}
				scrollbar={false}
				{...props}
			>
				{galleryItems?.map((media) => (
					<SwiperSlide
						key={media.type === "image" ? media.src : media.youtubeId}
					>
						{media.type === "image" ? (
							<ImageWrapper {...media} isDog={isDog} />
						) : (
							<CustomVideoComponent {...media} />
						)}
					</SwiperSlide>
				))}
			</Swiper>
		);
	},
);

export default MediaCarousel;
